import { computePosition, flip, shift } from '@floating-ui/dom';
import { type Editor, posToDOMRect, ReactRenderer } from '@tiptap/react';
import { type SuggestionOptions } from '@tiptap/suggestion';

import { type Mention } from '.';
import { MentionList } from './mention-list';

interface UpdatePositionProps {
  editor: Editor;
  element: HTMLElement;
}

const updatePosition = ({ editor, element }: UpdatePositionProps) => {
  const { view, state } = editor;
  const { from, to } = state.selection;

  const virtualElement = {
    getBoundingClientRect: () => posToDOMRect(view, from, to),
  };

  computePosition(virtualElement, element, {
    middleware: [shift(), flip()],
    placement: 'bottom-start',
    strategy: 'absolute',
  }).then(({ x, y, strategy }) => {
    element.style.left = `${Math.round(x)}px`;
    element.style.position = strategy;
    element.style.top = `${Math.round(y)}px`;
    element.style.width = 'max-content';
  });
};

interface SuggestionOptionsProps {
  items: string[];
}

export function suggestionOptions({ items }: SuggestionOptionsProps): Partial<SuggestionOptions> {
  return {
    char: '{{',

    items: ({ query }) => {
      return items.filter((item) => item.toLowerCase().includes(query.toLowerCase()));
    },

    command: ({ editor, range, props }) => {
      const { mention, default: defaultProp } = props as Mention;

      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent({ type: 'mention', attrs: { mention, default: defaultProp ?? null } })
        .run();
    },

    render: () => {
      let component: any;

      return {
        onStart: (props) => {
          const { editor, clientRect } = props;
          component = new ReactRenderer(MentionList, {
            props: { ...props, allItems: items },
            editor,
          });
          const element = component.element as HTMLElement;

          if (!clientRect) return;

          element.style.position = 'absolute';

          const editorElement = editor.view.dom.parentElement;
          editorElement?.appendChild(element);

          updatePosition({ editor, element });
        },

        onUpdate(props) {
          component?.updateProps(props);

          if (!props.clientRect || !component) return;

          updatePosition({ editor: props.editor, element: component.element });
        },

        onKeyDown(props) {
          if (!component) return false;

          if (props.event.key === 'Escape') {
            component.destroy();
            return true;
          }

          return component.ref?.onKeyDown(props) ?? false;
        },

        onExit() {
          component?.element?.remove();
          component?.destroy();
        },
      };
    },
  };
}
